import React, { useEffect, useState } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER, propTypes } from '../../util/types';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  PROFILE_PAGE_PENDING_APPROVAL_VARIANT,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
  isNotFoundError,
} from '../../util/errors';
import {
  getDetailCustomFieldValue,
  getFieldValue,
  pickCustomFieldProps,
} from '../../util/fieldHelpers';
import {
  getCurrentUserTypeRoles,
  hasPermissionToViewData,
  isUserAuthorized,
  profilePageParams,
} from '../../util/userHelpers';
import { richText } from '../../util/richText';
import { getRatingStats } from '../../util/reviews';
import { composedShareImage, withMarketplaceFallback } from '../../util/shareImage';
import { BUSINESS_ADDRESS_KEYS, formatBusinessAddress } from '../../util/userTypes';
import { contactVisibility } from '../../util/partnerContact';

import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  Page,
  AvatarLarge,
  AverageStarRating,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSingleColumn,
  NamedRedirect,
  CustomExtendedDataSection,
  LogoutButton,
  PartnerLine,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import ShareButton from '../ListingPage/ShareListing/ShareButton';
import { profileShareProps } from '../ListingPage/ShareListing/shareProps';

import css from './ProfilePage.module.css';

const MIN_LENGTH_FOR_LONG_WORDS = 20;

/**
 * Sidebar identity card: avatar, display name, bio, custom user fields,
 * edit link. Single component handling both mobile (stacked on top) and
 * desktop (sticky sidebar) — the responsive shape lives in CSS.
 */
export const AsideContent = props => {
  const {
    user,
    displayName,
    reviews = [],
    bio,
    publicData,
    metadata,
    userFieldConfig,
    intl,
    showLinkToProfileSettingsPage,
    shareProps,
    facebookAppId,
    onManageDisableScrolling,
  } = props;

  const hasBio = !!bio;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });

  const { average: rating, count: reviewCount, hasReviews } = getRatingStats(reviews);

  // Smooth-scroll from the rating summary down to the reviews section.
  // Progressive enhancement: the anchor's href="#reviews" still works
  // without JS; here we intercept to add smooth behavior + hash-free URL.
  const handleReviewsClick = e => {
    if (typeof document === 'undefined') {
      return;
    }
    const reviewsEl = document.getElementById('reviews');
    if (reviewsEl) {
      e.preventDefault();
      reviewsEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <aside className={css.aside}>
      <div className={css.header}>
        <AvatarLarge className={css.avatar} user={user} disableProfileLink />
        <div className={css.identity}>
          {displayName ? (
            <Heading as="h1" rootClassName={css.displayName}>
              {displayName}
            </Heading>
          ) : null}
          <PartnerLine user={user} />
          {/* Rating and share sit on one row: the rating is the thing worth
              sharing, and the button is the same soft disc as on a listing so
              it reads as the same action. */}
          <div className={css.ratingAndShare}>
            {hasReviews ? (
              <a
                className={css.ratingRow}
                href="#reviews"
                onClick={handleReviewsClick}
                aria-label={intl.formatMessage(
                  { id: 'ProfilePage.ratingAccessibility' },
                  { rating: rating.toFixed(1), outOf: 5, count: reviewCount }
                )}
              >
                <span aria-hidden="true">
                  <AverageStarRating rating={rating} />
                </span>
                <p className={css.ratingText} aria-hidden="true">
                  {intl.formatMessage(
                    { id: 'ProfilePage.rating' },
                    { rating: rating.toFixed(1), count: reviewCount }
                  )}
                </p>
              </a>
            ) : null}
            {shareProps ? (
              <ShareButton
                {...shareProps}
                facebookAppId={facebookAppId}
                onManageDisableScrolling={onManageDisableScrolling}
                className={css.shareButton}
              />
            ) : null}
          </div>
        </div>
      </div>
      {hasBio ? <p className={css.bio}>{bioWithLinks}</p> : null}
      {displayName ? (
        <CustomUserFields
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFieldConfig}
          intl={intl}
        />
      ) : null}
      {showLinkToProfileSettingsPage ? (
        <div className={css.ownProfileActions}>
          <NamedLink className={css.editLink} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
          <LogoutButton className={css.logoutLink} />
        </div>
      ) : null}
    </aside>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

/**
 * Reviews card: tabs across the top (provider vs customer reviews when
 * both roles exist), filtered list below. Single layout for mobile +
 * desktop — at this size the tab nav handles small screens gracefully.
 */
export const ReviewsSection = props => {
  const { reviews, queryReviewsError, userTypeRoles, intl } = props;
  const { customer: isCustomerUserType, provider: isProviderUserType } = userTypeRoles;

  const initialReviewState = !isProviderUserType
    ? REVIEW_TYPE_OF_CUSTOMER
    : REVIEW_TYPE_OF_PROVIDER;
  const [showReviewsType, setShowReviewsType] = useState(initialReviewState);

  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;

  const providerReviewsMaybe = isProviderUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.reviewsTabTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsFromMyCustomersTitle"
                values={{ count: reviewsOfProvider.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeProviderSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
        },
      ]
    : [];

  const customerReviewsMaybe = isCustomerUserType
    ? [
        {
          text: (
            <Heading as="h3" rootClassName={css.reviewsTabTitle}>
              <FormattedMessage
                id="ProfilePage.reviewsAsACustomerTitle"
                values={{ count: reviewsOfCustomer.length }}
              />
            </Heading>
          ),
          selected: isReviewTypeCustomerSelected,
          onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
        },
      ]
    : [];
  const reviewTabs = [...providerReviewsMaybe, ...customerReviewsMaybe];
  const hasMoreThanOneTab = reviewTabs.length > 1;

  return (
    <section id="reviews" className={css.reviewsSection}>
      <Heading as="h2" rootClassName={css.reviewsSectionTitle}>
        <FormattedMessage id="ProfilePage.reviewsSectionTitle" />
      </Heading>
      {hasMoreThanOneTab ? (
        <ButtonTabNavHorizontal
          className={css.reviewsTabNav}
          tabs={reviewTabs}
          ariaLabel={intl.formatMessage({ id: 'ProfilePage.screenreader.reviewsNav' })}
        />
      ) : reviewTabs[0] ? (
        <div className={css.reviewsTabNav}>{reviewTabs[0].text}</div>
      ) : null}

      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

      {isReviewTypeProviderSelected ? (
        <Reviews reviews={reviewsOfProvider} />
      ) : (
        <Reviews reviews={reviewsOfCustomer} />
      )}
    </section>
  );
};

// Which partner contact detail each profile row is (see util/partnerContact.js).
const CONTACT_FIELD_OF_KEY = {
  companyName: 'name',
  orgNumber: 'orgNumber',
  businessAddress: 'address',
  contactEmail: 'email',
  website: 'website',
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig, intl } = props;
  const isBusinessProfile = publicData?.userType === 'business';
  const visibility = contactVisibility(publicData);

  const shouldPickUserField = fieldConfig =>
    ['public', 'metadata'].includes(fieldConfig?.scope) &&
    fieldConfig?.showConfig?.displayInProfile !== false;
  // Fields declared as e-mail addresses are shown as mailto links.
  const emailFieldKeys = (userFieldConfig || [])
    .filter(config => config?.saveConfig?.validateAs === 'email')
    .map(config => config.key);
  const propsForCustomFields = (
    pickCustomFieldProps(
      { publicData, metadata },
      userFieldConfig,
      'userType',
      shouldPickUserField
    ) || []
  )
    .map(fieldProps =>
      emailFieldKeys.includes(fieldProps.key) ? { ...fieldProps, isEmail: true } : fieldProps
    )
    // A business address is three fields (street, postcode, city), shown as
    // one "Adresse" line where the first of them would have been.
    .reduce((fields, fieldProps) => {
      if (!BUSINESS_ADDRESS_KEYS.includes(fieldProps.key)) {
        return fields.concat(fieldProps);
      }
      const address = formatBusinessAddress(publicData);
      const alreadyShown = fields.some(f => f.key === 'businessAddress');
      return address && !alreadyShown
        ? fields.concat({
            schemaType: fieldProps.schemaType,
            key: 'businessAddress',
            heading: intl.formatMessage({ id: 'ProfilePage.businessAddressHeading' }),
            text: address,
          })
        : fields;
    }, [])
    // A Zuprize Partner chooses which contact details show (profile settings).
    .filter(fieldProps => {
      const field = CONTACT_FIELD_OF_KEY[fieldProps.key];
      return !isBusinessProfile || !field || visibility[field];
    })
    .concat(
      isBusinessProfile && visibility.phone && publicData.contactPhone
        ? [
            {
              schemaType: 'text',
              key: 'contactPhone',
              heading: intl.formatMessage({ id: 'ProfilePage.contactPhoneHeading' }),
              text: publicData.contactPhone,
            },
          ]
        : []
    );

  const pickUserFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, userTypeConfig = {}, showConfig = {} } = config;
    const { limitToUserTypeIds, userTypeIds } = userTypeConfig;
    const userType = publicData.userType;
    const isTargetUserType = !limitToUserTypeIds || userTypeIds.includes(userType);

    const { label, displayInProfile } = showConfig;
    const publicDataValue = getFieldValue(publicData, key);
    const metadataValue = getFieldValue(metadata, key);
    const value = publicDataValue !== null ? publicDataValue : metadataValue;

    if (displayInProfile && isTargetUserType && value !== null) {
      const detailValue = getDetailCustomFieldValue(
        enumOptions,
        value,
        schemaType,
        key,
        label,
        intl,
        'ProfilePage'
      );

      return detailValue ? filteredConfigs.concat(detailValue) : filteredConfigs;
    }
    return filteredConfigs;
  };
  const sectionDetailsProps = {
    ...props,
    fieldConfigs: userFieldConfig,
    heading: 'ProfilePage.detailsTitle',
    rootClassName: css.userFieldSection,
  };

  return (
    <CustomExtendedDataSection
      sectionDetailsProps={sectionDetailsProps}
      propsForCustomFields={propsForCustomFields}
      idPrefix="profilePage"
      pickExtendedDataFields={pickUserFields}
      rootClassName={css.userFieldSection}
    />
  );
};

/**
 * Right-hand column: listings grid + reviews card. The sidebar owns the
 * identity bits (avatar / name / bio / fields / edit link).
 */
export const MainContent = props => {
  const {
    userShowError,
    listings,
    queryListingsError,
    reviews = [],
    queryReviewsError,
    intl,
    hideReviews,
    userTypeRoles,
  } = props;

  const hasListings = listings.length > 0;

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }
  return (
    <div className={css.main}>
      <section className={css.listingsSection}>
        <Heading as="h2" rootClassName={css.sectionTitle}>
          <FormattedMessage id="ProfilePage.listingsTitle" values={{ count: listings.length }} />
        </Heading>
        {hasListings ? (
          <ul className={css.listings}>
            {listings.map(l => (
              <li className={css.listing} key={l.id.uuid}>
                {/* The profile page stays as it is: no Z on the photos here. */}
                <ListingCard listing={l} showAuthorInfo={false} showPartnerMark={false} />
              </li>
            ))}
          </ul>
        ) : (
          <p className={css.emptyState}>
            <FormattedMessage id="ProfilePage.noListings" />
          </p>
        )}
      </section>

      {hideReviews ? null : (
        <ReviewsSection
          reviews={reviews}
          queryReviewsError={queryReviewsError}
          userTypeRoles={userTypeRoles}
          intl={intl}
        />
      )}
    </div>
  );
};

/**
 * ProfilePageComponent
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether the scrolling is disabled
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {boolean} props.useCurrentUser - Whether to use the current user
 * @param {propTypes.user|propTypes.currentUser} props.user - The user
 * @param {propTypes.error} props.userShowError - The user show error
 * @param {propTypes.error} props.queryListingsError - The query listings error
 * @param {Array<propTypes.listing|propTypes.ownListing>} props.listings - The listings
 * @param {Array<propTypes.review>} props.reviews - The reviews
 * @param {propTypes.error} props.queryReviewsError - The query reviews error
 * @returns {JSX.Element} ProfilePageComponent
 */
export const ProfilePageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    scrollingDisabled,
    params: pathParams,
    currentUser,
    useCurrentUser,
    userShowError,
    user,
    ...rest
  } = props;
  const isVariant = pathParams.variant?.length > 0;
  const isPreview = isVariant && pathParams.variant === PROFILE_PAGE_PENDING_APPROVAL_VARIANT;

  // Stripe's onboarding needs a business URL for each seller, but the profile page can be
  // too empty for the provider at the time they are creating their first listing.
  // To remedy the situation, we redirect Stripe's crawler to the landing page of the marketplace.
  // TODO: When there's more content on the profile page, we should consider by-passing this redirection.
  const searchParams = rest?.location?.search;
  const isStorefront = searchParams
    ? new URLSearchParams(searchParams)?.get('mode') === 'storefront'
    : false;
  if (isStorefront) {
    return <NamedRedirect name="LandingPage" />;
  }

  // The route param can be either the vanity username or the UUID, so
  // match against both — otherwise a user viewing their own profile via
  // the /u/<username> URL wouldn't be recognised as the owner (and would
  // miss the edit-profile + logout controls).
  const currentUserUuid = currentUser?.id?.uuid;
  const currentUserUsername = currentUser?.attributes?.profile?.publicData?.username;
  const isCurrentUser =
    !!currentUserUuid &&
    (pathParams.id === currentUserUuid ||
      (!!currentUserUsername && pathParams.id === currentUserUsername));
  const profileUser = useCurrentUser ? currentUser : user;
  const { bio, displayName, publicData, metadata } = profileUser?.attributes?.profile || {};
  const { userFields } = config.user;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const isUnauthorizedOnPrivateMarketplace = isPrivateMarketplace && isUnauthorizedUser;
  const hasUserPendingApprovalError = isErrorUserPendingApproval(userShowError);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasNoViewingRightsOnPrivateMarketplace = isPrivateMarketplace && hasNoViewingRightsUser;

  const userTypeRoles = getCurrentUserTypeRoles(config, profileUser);

  const isDataLoaded = isPreview
    ? currentUser != null || userShowError != null
    : hasNoViewingRightsOnPrivateMarketplace
    ? currentUser != null || userShowError != null
    : user != null || userShowError != null;

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  // What a shared profile link previews as, and what the share button sends.
  // The listings here are the seller's published ones, newest first, so the
  // first with a photo is what a shared card shows — something they are
  // selling, rather than the marketplace's generic picture. A seller with
  // nothing up gets the marketplace image and a description that does not
  // say "0 plagg".
  const listings = rest.listings || [];
  const listingCount = listings.length;
  const newestWithPhoto = listings.find(l => l?.images?.[0]?.id?.uuid);
  const composed = composedShareImage(config.marketplaceRootURL, newestWithPhoto);
  const shareImagesMaybe = withMarketplaceFallback(composed ? [composed] : []);
  // "Se hva Guro selger på Zuprize" promises items, so a seller with nothing
  // up gets the neutral "Se profilen til Guro på Zuprize" and no count.
  const hasListings = listingCount > 0;
  const socialSharing = displayName
    ? {
        title: intl.formatMessage(
          {
            id: hasListings
              ? 'ProfilePage.socialSharingTitle'
              : 'ProfilePage.socialSharingTitleNoListings',
          },
          { name: displayName }
        ),
        description: hasListings
          ? intl.formatMessage(
              { id: 'ProfilePage.socialSharingDescription' },
              { count: listingCount }
            )
          : undefined,
      }
    : undefined;
  const shareProps = profileShareProps({
    user: profileUser,
    displayName,
    listingCount,
    isOwn: mounted && isCurrentUser,
    marketplaceRootURL: config.marketplaceRootURL,
    intl,
  });

  if (!isDataLoaded) {
    return null;
  } else if (!isPreview && isNotFoundError(userShowError)) {
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (!isPreview && (isUnauthorizedOnPrivateMarketplace || hasUserPendingApprovalError)) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (
    (!isPreview && hasNoViewingRightsOnPrivateMarketplace && !isCurrentUser) ||
    isErrorNoViewingPermission(userShowError)
  ) {
    // Someone without viewing rights on a private marketplace is trying to
    // view a profile page that is not their own – redirect to NoAccessPage
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  } else if (!isPreview && isForbiddenError(userShowError)) {
    // This can happen if private marketplace mode is active, but it's not reflected through asset yet.
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  } else if (isPreview && mounted && !isCurrentUser) {
    // Someone is manipulating the URL, redirect to current user's profile page.
    return isCurrentUser === false ? (
      <NamedRedirect name="ProfilePage" params={profilePageParams(currentUser)} />
    ) : null;
  } else if ((isPreview || isPrivateMarketplace) && !mounted) {
    // This preview of the profile page is not rendered on server-side
    // and the first pass on client-side should render the same UI.
    return null;
  }

  // This is rendering normal profile page (not preview for pending-approval)
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      // Sellers' /u/ profiles stay out of Google (they showed as sitelinks);
      // the partners' public pages are /butikker (PartnerPage), indexed.
      noIndex
      socialSharing={socialSharing}
      facebookImages={shareImagesMaybe}
      twitterImages={shareImagesMaybe}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        mainEntity: {
          '@type': 'Person',
          name: profileUser?.attributes?.profile?.displayName,
        },
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.pageContainer}>
          <AsideContent
            user={profileUser}
            displayName={displayName}
            reviews={rest.reviews}
            bio={bio}
            publicData={publicData}
            metadata={metadata}
            userFieldConfig={userFields}
            intl={intl}
            showLinkToProfileSettingsPage={mounted && isCurrentUser}
            shareProps={shareProps}
            facebookAppId={config.facebookAppId}
            onManageDisableScrolling={rest.onManageDisableScrolling}
          />
          <MainContent
            userShowError={userShowError}
            hideReviews={hasNoViewingRightsOnPrivateMarketplace}
            intl={intl}
            userTypeRoles={userTypeRoles}
            {...rest}
          />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews = [],
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;

  // Show currentUser's data if it's not approved yet
  const isCurrentUser = userId?.uuid === currentUser?.id?.uuid;
  const useCurrentUser =
    isCurrentUser && !(isUserAuthorized(currentUser) && hasPermissionToViewData(currentUser));

  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    useCurrentUser,
    user,
    userShowError,
    queryListingsError,
    listings: getMarketplaceEntities(state, userListingRefs),
    reviews,
    queryReviewsError,
  };
};

const mapDispatchToProps = dispatch => ({
  // The share dialog is a Modal, which locks page scroll while open.
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const ProfilePage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(ProfilePageComponent);

export default ProfilePage;
